/**
 * SubmittalChecklistEditor — submittal review checklist templates (Submittal Review Tool
 * Phase 2, docs/SUBMITTAL_REVIEW_PLAN.md). A tab inside TemplatesWorkspace.jsx (consistent with
 * the checklist-templates module): a templated checklist per submittal subject matter (e.g.
 * "Windows and Doors") for the team to run through when reviewing a construction submittal — a
 * Supabase table, editable here, no deploy required. Authoring/template side only — nothing
 * consumes these yet; the runtime review tool (Phase 5) is a separate, later build.
 *
 * Self-contained list (mirrors PunchRoomGuidanceList's own grouped-list-with-inline-add pattern)
 * — no separate drill-down editor screen; each template expands in place to show/edit its items.
 */
import { useState, useEffect } from 'react'
import { S } from './TemplatesWorkspace.jsx'
import { confirmDialog } from './Dialog.jsx'
import {
  getSubmittalChecklistTemplates, createSubmittalChecklistTemplate, renameSubmittalChecklistTemplate,
  deleteSubmittalChecklistTemplate, moveSubmittalChecklistTemplate, createSubmittalChecklistItem,
  updateSubmittalChecklistItem, deleteSubmittalChecklistItem, moveSubmittalChecklistItem,
} from '../services/submittalChecklists.js'

function prefixesToText(arr) {
  return (arr || []).join(', ')
}
function textToPrefixes(text) {
  return String(text || '').split(',').map(s => s.trim()).filter(Boolean)
}

function ChecklistItemRow({ item, onSave, onDelete, onMove, isFirst, isLast }) {
  const [text, setText] = useState(item.text)
  const dirty = text.trim() !== item.text && text.trim().length > 0

  async function commit() {
    const t = text.trim()
    if (!t || t === item.text) { setText(item.text); return }
    await onSave(t)
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '5px 4px', borderBottom: '0.5px solid var(--aj-border)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 3 }}>
        <button onClick={() => onMove('up')} disabled={isFirst} title="Move up"
          style={{ background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer', color: isFirst ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', fontSize: 10, padding: 0, lineHeight: 1 }}>▲</button>
        <button onClick={() => onMove('down')} disabled={isLast} title="Move down"
          style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer', color: isLast ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', fontSize: 10, padding: 0, lineHeight: 1 }}>▼</button>
      </div>
      <textarea
        value={text}
        rows={Math.max(1, Math.ceil(text.length / 90))}
        onChange={e => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.target.blur() } }}
        style={{
          flex: 1, border: 'none', outline: 'none', resize: 'none', background: 'transparent',
          fontSize: 12.5, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)', lineHeight: 1.45,
          borderBottom: dirty ? '1px dashed var(--aj-border-mid)' : '1px solid transparent',
        }}
      />
      <button onClick={onDelete} title="Delete item" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 13, padding: '2px 4px' }}>×</button>
    </div>
  )
}

function TemplateRow({ template, expanded, onToggle, onRename, onDelete, onMove, onItemsChange, isFirst, isLast, addToast }) {
  const [name, setName] = useState(template.name)
  const [prefixesText, setPrefixesText] = useState(prefixesToText(template.sectionPrefixes))
  const [newText, setNewText] = useState('')
  const [adding, setAdding] = useState(false)

  useEffect(() => { setName(template.name) }, [template.name])
  useEffect(() => { setPrefixesText(prefixesToText(template.sectionPrefixes)) }, [template.sectionPrefixes])

  async function commitName() {
    const n = name.trim()
    if (!n || n === template.name) { setName(template.name); return }
    try { await onRename({ name: n }) } catch (e) { addToast('Rename failed: ' + (e.message || 'unknown error')); setName(template.name) }
  }

  async function commitPrefixes() {
    const next = textToPrefixes(prefixesText)
    const current = template.sectionPrefixes || []
    if (next.length === current.length && next.every((v, i) => v === current[i])) {
      setPrefixesText(prefixesToText(current)); return
    }
    try { await onRename({ sectionPrefixes: next }) } catch (e) { addToast('Save failed: ' + (e.message || 'unknown error')); setPrefixesText(prefixesToText(current)) }
  }

  async function addItem() {
    const t = newText.trim()
    if (!t || adding) return
    setAdding(true)
    try {
      const item = await createSubmittalChecklistItem(template.id, t)
      onItemsChange([...template.items, item])
      setNewText('')
    } catch (e) { addToast('Add failed: ' + (e.message || 'unknown error')) } finally { setAdding(false) }
  }

  async function saveItem(item, text) {
    try {
      await updateSubmittalChecklistItem(item.id, text)
      onItemsChange(template.items.map(i => i.id === item.id ? { ...i, text } : i))
    } catch (e) { addToast('Save failed: ' + (e.message || 'unknown error')) }
  }

  async function deleteItem(item) {
    if (!(await confirmDialog({ title: 'Delete item?', message: 'Delete "' + item.text + '"?', confirmLabel: 'Delete', danger: true }))) return
    try {
      await deleteSubmittalChecklistItem(item.id)
      onItemsChange(template.items.filter(i => i.id !== item.id))
    } catch (e) { addToast('Delete failed: ' + (e.message || 'unknown error')) }
  }

  async function moveItem(item, direction) {
    const idx = template.items.findIndex(i => i.id === item.id)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= template.items.length) return
    const next = [...template.items]
    ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
    onItemsChange(next)
    try { await moveSubmittalChecklistItem(item.id, direction) } catch (e) { addToast('Reorder failed') }
  }

  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 10px 8px', borderBottom: '0.5px solid var(--aj-border-mid)', flexWrap: 'wrap' }}>
        <button onClick={onToggle} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 11, padding: 0, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</button>
        <input value={name} onChange={e => setName(e.target.value)} onBlur={commitName}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
          style={{ ...S.input, width: 200, fontWeight: 500, fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'Montserrat, sans-serif', border: 'none', background: 'transparent', padding: '2px 0' }} />
        <span style={{ fontSize: 10.5, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-muted)' }}>{template.items.length} item{template.items.length !== 1 ? 's' : ''}</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => onMove('up')} disabled={isFirst} title="Move template up"
          style={{ background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer', color: isFirst ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', fontSize: 12, padding: '0 3px' }}>▲</button>
        <button onClick={() => onMove('down')} disabled={isLast} title="Move template down"
          style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer', color: isLast ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', fontSize: 12, padding: '0 3px' }}>▼</button>
        <button onClick={onDelete} title="Delete template" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 14, padding: '0 4px' }}>×</button>
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
          <span style={{ fontSize: 10.5, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text-muted)', whiteSpace: 'nowrap' }}>Section prefixes</span>
          <input value={prefixesText} onChange={e => setPrefixesText(e.target.value)} onBlur={commitPrefixes}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
            placeholder="e.g. 08 1, 08 5"
            style={{ ...S.input, flex: 1, maxWidth: 320, fontSize: 11.5, fontFamily: 'DM Mono, monospace', padding: '3px 8px' }} />
        </div>
      </div>
      {expanded && (
        <div style={{ padding: '4px 10px 10px 30px' }}>
          {template.items.map((item, i) => (
            <ChecklistItemRow key={item.id} item={item}
              isFirst={i === 0} isLast={i === template.items.length - 1}
              onSave={text => saveItem(item, text)}
              onDelete={() => deleteItem(item)}
              onMove={dir => moveItem(item, dir)} />
          ))}
          {template.items.length === 0 && (
            <div style={{ padding: '8px 0', fontSize: 12, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No checklist items yet.</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-start' }}>
            <textarea value={newText} rows={1} placeholder="Add a checklist item…"
              onChange={e => setNewText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addItem() } }}
              style={{ ...S.input, flex: 1, resize: 'vertical' }} />
            <button onClick={addItem} disabled={adding || !newText.trim()} style={S.btnSecondary}>{adding ? 'Adding…' : '+ Add'}</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function SubmittalChecklistList({ addToast }) {
  const [templates, setTemplates] = useState(null)   // null = loading
  const [expanded, setExpanded] = useState({})
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => { getSubmittalChecklistTemplates().then(setTemplates).catch(() => setTemplates([])) }, [])

  async function handleCreate() {
    const name = newName.trim()
    if (!name || creating) return
    setCreating(true)
    try {
      const t = await createSubmittalChecklistTemplate(name)
      setTemplates(prev => [...(prev || []), t])
      setExpanded(e => ({ ...e, [t.id]: true }))
      setNewName('')
      addToast('Checklist template created')
    } catch (e) { addToast('Create failed: ' + (e.message || 'unknown error')) } finally { setCreating(false) }
  }

  function patchTemplate(id, patch) {
    setTemplates(prev => (prev || []).map(t => t.id === id ? { ...t, ...patch } : t))
  }

  async function handleDelete(t) {
    if (!(await confirmDialog({
      title: 'Delete checklist template?',
      message: `Delete "${t.name}" and its ${t.items.length} checklist item(s)? This cannot be undone.`,
      confirmLabel: 'Delete', danger: true,
    }))) return
    try {
      await deleteSubmittalChecklistTemplate(t.id)
      setTemplates(prev => (prev || []).filter(x => x.id !== t.id))
      addToast('Checklist template deleted')
    } catch (e) { addToast('Delete failed: ' + (e.message || 'unknown error')) }
  }

  async function handleMoveTemplate(t, direction) {
    const list = templates || []
    const idx = list.findIndex(x => x.id === t.id)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= list.length) return
    const next = [...list]
    ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
    setTemplates(next)
    try { await moveSubmittalChecklistTemplate(t.id, direction) } catch (e) { addToast('Reorder failed') }
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '0 24px 12px', flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          style={{ ...S.input, width: 260, display: 'inline-block' }}
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleCreate() }}
          placeholder="New checklist template name…"
        />
        <button onClick={handleCreate} disabled={creating || !newName.trim()}
          style={{ ...S.btnPrimary, opacity: (creating || !newName.trim()) ? 0.45 : 1, cursor: (creating || !newName.trim()) ? 'not-allowed' : 'pointer' }}>
          {creating ? 'Creating…' : '+ New Template'}
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
        {templates === null && (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>Loading checklist templates…</div>
        )}
        {Array.isArray(templates) && templates.length === 0 && (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
            No submittal checklist templates yet — add one above (e.g. Windows and Doors, Cabinetry, Roofing).
          </div>
        )}
        {(templates || []).map((t, i) => (
          <TemplateRow
            key={t.id}
            template={t}
            expanded={!!expanded[t.id]}
            onToggle={() => setExpanded(e => ({ ...e, [t.id]: !e[t.id] }))}
            onRename={patch => renameSubmittalChecklistTemplate(t.id, patch.name, patch.sectionPrefixes).then(saved => patchTemplate(t.id, {
              name: saved.name !== undefined ? saved.name : t.name,
              sectionPrefixes: saved.sectionPrefixes !== undefined ? saved.sectionPrefixes : t.sectionPrefixes,
            }))}
            onDelete={() => handleDelete(t)}
            onMove={dir => handleMoveTemplate(t, dir)}
            onItemsChange={items => patchTemplate(t.id, { items })}
            isFirst={i === 0}
            isLast={i === (templates || []).length - 1}
            addToast={addToast}
          />
        ))}
      </div>
    </div>
  )
}
